import Component from '@glimmer/component';
import { service } from '@ember/service';
import { Section } from 'tio-ui/components/layout';
import { Text } from 'tio-ui/components/typography';
import { t } from 'ember-intl';
import { getVisibleFields } from '../../../utils/tuition-assistance/financial-details-config.ts';
import type TasApplicationModel from '../../../models/tas-application.ts';
import type { LimitsStatusDataSignature } from '../../../services/tuition-assistance.ts';
import type IntlService from 'ember-intl/services/intl';

export interface FinancialDetailsAdminSummarySignature {
  Args: {
    application: TasApplicationModel;
    limitsData?: LimitsStatusDataSignature;
  };
  Element: HTMLElement;
}

export default class FinancialDetailsAdminSummary extends Component<FinancialDetailsAdminSummarySignature> {
  @service declare intl: IntlService;

  get visibleFields() {
    return getVisibleFields(this.args.application, this.args.limitsData);
  }

  formatFieldValue = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return this.intl.t('common.not_applicable');
    }

    const normalizedValue = Number(value);
    if (Number.isNaN(normalizedValue)) {
      return this.intl.t('common.not_applicable');
    }

    return this.intl.formatNumber(normalizedValue / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  };

  <template>
    <Section class="" data-test-financial-details-admin-summary>
      <:body>
        <div class="max-w-3xl">
          <div
            class="flex items-center justify-between px-4 py-2.5 bg-gray-100/50 border border-gray-200/80 rounded-[10px]"
          >
            <Text class="text-base font-semibold text-gray-900 dark:text-gray-100">
              {{t "common.tas.financial_details_summary.title"}}
            </Text>
          </div>
          <div class="mt-2">
            {{#each this.visibleFields as |field|}}
              <div
                class="flex py-4 px-4 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                data-test-financial-details-field={{field.key}}
              >
                <div class="w-64 md:w-72 flex-shrink-0">
                  <Text
                    class="text-sm font-medium text-gray-600 dark:text-gray-400 md:whitespace-nowrap"
                  >
                    {{t field.labelKey}}
                  </Text>
                </div>
                <div class="flex-1">
                  <Text class="text-sm font-medium text-gray-700 dark:text-gray-200 pl-6">
                    {{this.formatFieldValue field.value}}
                  </Text>
                </div>
              </div>
            {{/each}}
          </div>
        </div>
      </:body>
    </Section>
  </template>
}
